import { isAxiosError } from "axios"

const FALLBACK_MESSAGE = "خطایی رخ داد. لطفاً دوباره تلاش کنید."

interface ApiErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown }
}

function isApiErrorEnvelope(data: unknown): data is ApiErrorEnvelope {
  return typeof data === "object" && data !== null && "error" in data
}

function getEnvelope(error: unknown): ApiErrorEnvelope["error"] | undefined {
  if (isAxiosError(error) && isApiErrorEnvelope(error.response?.data)) {
    return error.response?.data.error
  }
  return undefined
}

export function getApiErrorMessage(error: unknown): string {
  return getEnvelope(error)?.message ?? FALLBACK_MESSAGE
}

/**
 * The `error.code` from the API error envelope (`invalid`, `unauthorized`,
 * `forbidden`, `password_change_required`, `not_found`, `conflict`,
 * `too_many_requests`, `unavailable`, `internal`). A bodyless 429 gives
 * `"too_many_requests"`, and an unrecognised error gives `undefined`.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  const code = getEnvelope(error)?.code
  if (code) return code
  if (isAxiosError(error) && error.response?.status === 429) return "too_many_requests"
  return undefined
}

/**
 * The `error.details` map from a validation failure (`{ "<field label>": "<message>" }`),
 * or `undefined` when absent or not a string map.
 */
export function getApiErrorDetails(error: unknown): Record<string, string> | undefined {
  const details = getEnvelope(error)?.details
  if (typeof details !== "object" || details === null || Array.isArray(details)) return undefined
  const entries = Object.entries(details).filter((entry): entry is [string, string] => typeof entry[1] === "string")
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}
